import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import {DocumentNode} from 'graphql';
import React, {useState} from 'react';

import isArchived from 'archive/isArchived';
import * as BenchlingUtil from 'BenchlingUtil';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import notifications from 'notifications';
import QueryingAddSchemaInterfaceModal, {
  AddSchemaInterfaceRequestSubmitParams,
} from 'schemas/schema-interface/AddSchemaInterfaceModal';
import RemoveSchemaInterfaceModal from 'schemas/schema-interface/RemoveSchemaInterfaceModal';
import {
  schemaFieldsetName,
  schemaFieldsetNamePluralized,
} from 'schemas/schema-interface/util/schemaFieldsetName';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import * as LegacyModalContainer from 'ui-library/LegacyModalContainer';
import legacyRunModal from 'ui-library/legacyRunModal';
import Menu from 'ui-library/menus/Menu';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import ReorderableSimpleTable from 'ui-library/reorderable/ReorderableSimpleTable';
import {Row} from 'ui-library/reorderable/ReorderableSimpleTableConstants';
import {capitalize} from 'util/NameUtil';
import {withLoadingScreen} from 'util/Promises';

import {SchemaInterfaceListingGraphQL} from '../../../graphql-fragments/schemas/schema-interfaces/__generated/SchemaInterfaceListingGraphQL';
import {CreateSchemaInterfaceTableQuery} from './__generated/CreateSchemaInterfaceTableQuery';
import {EditSchemaInterfaceTableQuery} from './__generated/EditSchemaInterfaceTableQuery';
import {ImplementingSchemaInfo as AddSchemaInterfaceModalTagSchema} from './AddSchemaInterfaceModal';
import {
  removeInterfaceSchemaAssociation,
  schemaInterfaceURL,
  addInterfaceAndConformSchema,
} from './SchemaInterfaceDAO';
import {
  getInterfaceConstraintErrorsForSchema,
  InterfaceConstraintErrorDisplayDataForSchema,
} from './validation/SchemaInterfaceValidationUtils';

type QueryTagSchemaOrInaccessibleInfo = EditSchemaInterfaceTableQuery['tagSchema'];

type QueryTagSchema = ExtractByTypename<QueryTagSchemaOrInaccessibleInfo, 'TagSchemaNode'>;

export const styles = StyleSheet.create({
  singleButton: {marginLeft: '6px', marginRight: '6px', padding: 0},
  alwaysVisible: {visibility: 'visible'},
  rowLink: {
    display: 'block',
    textDecoration: 'inherit',
  },
});

type SchemaInterfaceListing = Pick<SchemaInterfaceListingGraphQL, 'id' | 'name' | 'archiveRecord'>;

interface SchemaInterfaceTableProps {
  writable?: boolean;
  tagSchema: AddSchemaInterfaceModalTagSchema;
  availableSchemaInterfaces: Array<SchemaInterfaceListing>;
  displayedSchemaInterfaces?: Array<SchemaInterfaceListing>;
  onSchemaInterfaceSubmit?: (params: AddSchemaInterfaceRequestSubmitParams) => Promise<void>;
  onSchemaInterfaceRemove?: (id: string) => Promise<void>;
  isLoading?: boolean;
}

export default function SchemaInterfaceTable(props: SchemaInterfaceTableProps): JSX.Element {
  const {
    writable = false,
    availableSchemaInterfaces,
    displayedSchemaInterfaces = [],
    onSchemaInterfaceSubmit = () => Promise.resolve(),
    onSchemaInterfaceRemove = () => Promise.resolve(),
    isLoading = false,
  } = props;
  // Only used for updating the dropdown value display
  const [selected, setSelected] = useState<SchemaInterfaceListing | null>(null);
  const [isDropdownVisible, setIsDropdownVisible] = useState<boolean>(false);

  const addSchemaInterfaceDropdown = (): void => {
    // Only permit a new dropdown if one does not currently exist
    if (!isDropdownVisible && !selected) {
      setIsDropdownVisible(true);
    }
  };

  const removeSchemaInterfaceDropdown = (): void => {
    setSelected(null);
    setIsDropdownVisible(false);
  };

  const getSchemaInterfaceOptions = (): Array<SchemaInterfaceListing> => {
    const displayedSchemaInterfaceIds = new Set(displayedSchemaInterfaces.map(({id}) => id));
    return availableSchemaInterfaces.filter(({id}) => !displayedSchemaInterfaceIds.has(id));
  };

  const getSchemaInterfaceDropdownRow = (): Row => ({
    id: 'schemaInterfaceDropdown',
    cells: [
      {
        key: 'schemaInterfaceDropdown:name',
        contents: (
          <>
            <SelectDropdown
              disabled={false}
              displayName={selected?.name ?? 'Select...'}
              closeOnInsideClick={true}
              closeOnOutsideClick={true}
              menu={
                <SearchableMenu
                  onSelect={selectSchemaInterface}
                  optionDatas={getSchemaInterfaceOptions().map((schemaInterface: SchemaInterfaceListing) => ({
                    value: schemaInterface.id,
                    name: schemaInterface.name,
                  }))}
                  selectedValue={selected?.id}
                  width={Menu.WIDTHS.FULL}
                />
              }
            />
          </>
        ),
      },
      {
        key: 'schemaInterfaceDropdown:spacer',
        contents: null,
      },
    ],
    actionGroup: (
      <div className={classNames(css(styles.alwaysVisible))}>
        <Tooltip placement={FloatingPlacement.TOP} text="Cancel">
          <span
            className={classNames(css(styles.singleButton), 'metadata-action faded u-danger-pointer')}
            onClick={removeSchemaInterfaceDropdown}
          >
            <BenchlingIcon className="metadata-action-icon--close" icon="close" />
          </span>
        </Tooltip>
      </div>
    ),
  });

  const selectSchemaInterface = (schemaInterfaceId: string): void => {
    const selectedSchemaInterface = availableSchemaInterfaces.find(({id}) => id === schemaInterfaceId);
    if (!selectedSchemaInterface) {
      return;
    }
    setSelected(selectedSchemaInterface);
    submitSchemaInterfaceSelection(schemaInterfaceId);
  };

  const submitSchemaInterfaceSelection = (schemaInterfaceId: string): void => {
    const onRequestClose = (): void => {
      removeSchemaInterfaceDropdown();
      LegacyModalContainer.close(QueryingAddSchemaInterfaceModal);
    };

    const onRequestSubmit = async (params: AddSchemaInterfaceRequestSubmitParams): Promise<void> => {
      await onSchemaInterfaceSubmit(params);
      onRequestClose();
    };

    LegacyModalContainer.open(
      QueryingAddSchemaInterfaceModal,
      <QueryingAddSchemaInterfaceModal
        onRequestClose={onRequestClose}
        onRequestSubmit={onRequestSubmit}
        schemaInterfaceId={schemaInterfaceId}
        tagSchema={props.tagSchema}
      />
    );
  };

  const getSchemaInterfaceRow = (schemaInterface: SchemaInterfaceListing): Row => ({
    id: schemaInterface.name,
    cells: [
      {
        key: `${schemaInterface.id}:name`,
        contents: (
          <a
            href={schemaInterfaceURL(schemaInterface.id)}
            className={css(styles.rowLink)}
            onClick={BenchlingUtil.navigate}
          >
            {schemaInterface.name}
          </a>
        ),
      },
    ],
    actionGroup: writable ? (
      <a
        className={classNames(css(styles.singleButton), 'metadata-action faded u-danger-pointer')}
        onClick={() => {
          onSchemaInterfaceRemove(schemaInterface.id);
        }}
      >
        <BenchlingIcon icon="delete" />
      </a>
    ) : null,
  });

  const getLoadingRow = (): Row => ({
    id: '_loading_row',
    cells: [{key: 'loading_row', contents: <div className={css(styles.rowLink)}>Loading...</div>}],
    actionGroup: null,
  });

  const getRows = (): Array<Row> =>
    isLoading
      ? [getLoadingRow()]
      : [
          ...displayedSchemaInterfaces
            .map((i: SchemaInterfaceListing) => getSchemaInterfaceRow(i))
            .sort(({id: id1}, {id: id2}) => id1.toLowerCase().localeCompare(id2.toLowerCase())),
          ...(isDropdownVisible ? [getSchemaInterfaceDropdownRow()] : []),
        ];

  return (
    <div className="u-large-spacing-bottom">
      <ReorderableSimpleTable
        headerCells={[
          {
            key: 'name',
            contents: (
              <>
                {capitalize(schemaFieldsetNamePluralized())}
                {writable && (
                  <BenchlingIcon
                    className={classNames({
                      'simpleDivTable-addButton': true,
                    })}
                    icon="compose"
                    onClick={addSchemaInterfaceDropdown}
                  />
                )}
              </>
            ),
            cellClassName: 'simpleDivTable-header--title',
          },
        ]}
        isDraggingDisabled={true}
        noRowsDisplay={`No ${schemaFieldsetNamePluralized()} implemented`}
        rows={getRows()}
        dataTestAttrs={{component: 'SchemaInterfaceTable', element: 'root'}}
      />
    </div>
  );
}

export const EDIT_SCHEMA_INTERFACE_TABLE_QUERY: DocumentNode = gql`
  query EditSchemaInterfaceTableQuery($schemaId: StrictString!, $organizationId: StrictString)
  @skipAudit
  @engComponent(name: SCHEMAS) {
    allSchemaInterfacesInOrg: schemaInterfaces(organizationId: $organizationId) {
      ...SchemaInterfaceListingGraphQL
    }
    tagSchema(id: $schemaId) {
      ... on TagSchemaNode {
        id
        interfaces {
          ...SchemaInterfaceListingGraphQL
        }
        prefixSource {
          id
          name
          prefix
        }
        allFieldsNodes {
          id
          appliedFieldset {
            name
            id
          }
        }
      }
    }
  }
  ${getFragmentWrapper('SchemaInterfaceListing').fragment}
`;

interface EditSchemaInterfaceTableProps {
  schemaId: string | null;
  organizationId: string;
  setInterfaceConstraintErrors: (errors: Array<InterfaceConstraintErrorDisplayDataForSchema>) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  updateSchemaInterfacesCallbackFn?: () => Promise<any>;
  writable?: boolean;
  tagSchema: AddSchemaInterfaceModalTagSchema;
}

function isQueryTagSchema(value: QueryTagSchemaOrInaccessibleInfo): value is QueryTagSchema {
  return value.__typename === 'TagSchemaNode';
}

export function EditSchemaInterfaceTable(props: EditSchemaInterfaceTableProps): JSX.Element | null {
  const {schemaId, organizationId, writable, setInterfaceConstraintErrors, tagSchema} = props;
  const queryResult = useBenchlingQuery<EditSchemaInterfaceTableQuery>(EDIT_SCHEMA_INTERFACE_TABLE_QUERY, {
    fetchPolicy: 'cache-and-network',
    variables: {
      schemaId,
      organizationId,
    },
  });
  if (queryResult.type === QueryResultType.ERROR) {
    // If the query is unsuccessful, no schema interface can be selected and none will be displayed
    return null;
  }

  let allSchemaInterfacesInOrg: Array<SchemaInterfaceListing>;
  let availableSchemaInterfaces: Array<SchemaInterfaceListing>;
  let displayedSchemaInterfaces: Array<SchemaInterfaceListing>;
  let prefixSource: QueryTagSchema['prefixSource'] | null;

  if (queryResult.type === QueryResultType.PENDING) {
    allSchemaInterfacesInOrg = [];
    availableSchemaInterfaces = [];
    displayedSchemaInterfaces = [];
    prefixSource = null;
  } else {
    allSchemaInterfacesInOrg = queryResult.data.allSchemaInterfacesInOrg;
    availableSchemaInterfaces = allSchemaInterfacesInOrg.filter(
      (schemaInterface: SchemaInterfaceListing) => !isArchived(schemaInterface)
    );
    displayedSchemaInterfaces = isQueryTagSchema(queryResult.data.tagSchema)
      ? (queryResult.data.tagSchema.interfaces.filter(
          (x) => x.__typename === 'SchemaInterfaceNode'
        ) as Array<SchemaInterfaceListing>)
      : [];
    prefixSource = isQueryTagSchema(queryResult.data.tagSchema)
      ? queryResult.data.tagSchema.prefixSource
      : null;
  }

  const addSchemaInterface = async (params: AddSchemaInterfaceRequestSubmitParams): Promise<void> => {
    const schemaInterfaceId = params.schemaInterface.id;
    const fieldsUpdate = params.conformityUpdate.fields;

    if (!schemaId) {
      return;
    }
    await withLoadingScreen(
      (async () => {
        // Any new computed fields will be added to the schema by the backend,
        // propagated from the interface. So we remove new computed fields
        // from the request payload, since creating computed fields directly
        // through this API is not supported.
        const fields = fieldsUpdate.filter(
          (field) => field.isComputed === false || (field.isComputed && field.id)
        );
        await addInterfaceAndConformSchema(schemaId, {fields}, [schemaInterfaceId])
          .then(async () => {
            notifications.success(`${capitalize(schemaFieldsetName())} added to schema successfully.`);
            await queryResult.refetch();
            await props.updateSchemaInterfacesCallbackFn?.();
          })
          .catch((e) => {
            if (e.responseJSON?.interfaceConstraintError) {
              const interfaceConstraintErrors = getInterfaceConstraintErrorsForSchema(e.responseJSON);
              if (interfaceConstraintErrors) {
                setInterfaceConstraintErrors(interfaceConstraintErrors);
              }
            }
            notifications.notifyError(e);
          });
      })(),
      'Updating...'
    );
  };

  const submitRemoveSchemaInterfaceRequest = async (
    schemaId: string,
    schemaInterfaceId: string,
    schemaPrefixReplacement?: string
  ): Promise<void> => {
    await removeInterfaceSchemaAssociation(schemaId, schemaInterfaceId, schemaPrefixReplacement);
    notifications.success(`${capitalize(schemaFieldsetName())} removed from schema successfully.`);
  };

  const refresh = async (): Promise<void> => {
    await withLoadingScreen(
      (async () => {
        await queryResult.refetch();
        await props.updateSchemaInterfacesCallbackFn?.();
      })(),
      'Updating...'
    );
  };

  const removeSchemaInterface = async (schemaInterfaceId: string): Promise<void> => {
    if (!schemaId) {
      return;
    }
    if (prefixSource?.id === schemaInterfaceId) {
      let requestClosed = false;
      // Removing the interface which is the source of the schema's prefix
      await legacyRunModal(RemoveSchemaInterfaceModal, {
        prefixSource,
        onRequestSubmit: async (replacementPrefix: string) => {
          await submitRemoveSchemaInterfaceRequest(schemaId, schemaInterfaceId, replacementPrefix);
        },
        onRequestClose: () => {
          requestClosed = true;
        },
      });
      if (requestClosed) {
        return;
      }
    } else {
      await submitRemoveSchemaInterfaceRequest(schemaId, schemaInterfaceId);
    }
    await refresh();
  };

  return (
    <SchemaInterfaceTable
      writable={writable}
      tagSchema={tagSchema}
      availableSchemaInterfaces={availableSchemaInterfaces}
      displayedSchemaInterfaces={displayedSchemaInterfaces}
      onSchemaInterfaceSubmit={addSchemaInterface}
      onSchemaInterfaceRemove={removeSchemaInterface}
      isLoading={queryResult.type === QueryResultType.PENDING}
    />
  );
}

export const CREATE_SCHEMA_INTERFACE_TABLE_QUERY: DocumentNode = gql`
  query CreateSchemaInterfaceTableQuery($organizationId: StrictString)
  @skipAudit
  @engComponent(name: SCHEMAS) {
    allSchemaInterfacesInOrg: schemaInterfaces(organizationId: $organizationId) {
      ...SchemaInterfaceListingGraphQL
    }
  }
  ${getFragmentWrapper('SchemaInterfaceListing').fragment}
`;

interface CreateSchemaInterfaceTableProps {
  writable?: boolean;
  organizationId: string;
  queuedSchemaInterfaces: Array<string>;
  onSchemaInterfaceEnqueue?: (params: AddSchemaInterfaceRequestSubmitParams) => Promise<void>;
  onSchemaInterfaceDequeue?: (id: string) => Promise<void>;
  tagSchema: AddSchemaInterfaceModalTagSchema;
}

export function CreateSchemaInterfaceTable(props: CreateSchemaInterfaceTableProps): JSX.Element | null {
  const {
    writable,
    organizationId,
    queuedSchemaInterfaces,
    onSchemaInterfaceEnqueue,
    onSchemaInterfaceDequeue,
    tagSchema,
  } = props;
  const queuedSet = new Set(queuedSchemaInterfaces);
  const queryResult = useBenchlingQuery<CreateSchemaInterfaceTableQuery>(
    CREATE_SCHEMA_INTERFACE_TABLE_QUERY,
    {
      fetchPolicy: 'cache-and-network',
      variables: {
        organizationId,
      },
    }
  );
  if (queryResult.type !== QueryResultType.SUCCESS) {
    // If the query is unsuccessful, no schema interface can be selected and none will be displayed
    return null;
  }
  const allSchemaInterfacesInOrg = queryResult.data.allSchemaInterfacesInOrg;
  const availableSchemaInterfaces: Array<SchemaInterfaceListing> = allSchemaInterfacesInOrg.filter(
    (schemaInterface: SchemaInterfaceListing) => !isArchived(schemaInterface)
  );

  return (
    <SchemaInterfaceTable
      writable={writable}
      tagSchema={tagSchema}
      availableSchemaInterfaces={availableSchemaInterfaces}
      displayedSchemaInterfaces={allSchemaInterfacesInOrg.filter((i: SchemaInterfaceListing) =>
        queuedSet.has(i.id)
      )}
      onSchemaInterfaceSubmit={onSchemaInterfaceEnqueue}
      onSchemaInterfaceRemove={onSchemaInterfaceDequeue}
    />
  );
}
